/**
 * Wave 29 Chunk 2 — Team module list page.
 *
 * HR (owner/admin) sees every staff member with search + filter chips.
 * Non-HR users are redirected to /me (they can't see anyone but themselves).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, X, Loader2, Users as UsersIcon, ShieldCheck, FileText,
  CloudDownload, AlertTriangle, Clock, Trophy, Megaphone, FileSignature,
  CalendarDays, Briefcase,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ROLE_CHIPS = [
  { value: "", label: "All" },
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "rep", label: "Rep" },
  { value: "technician", label: "Technician" },
];

export default function Team() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [status, setStatus] = useState(null);

  // Redirect non-HR straight to their personal portal — the list page is HR-only.
  useEffect(() => {
    if (user && !isHr) nav("/me", { replace: true });
  }, [user, isHr, nav]);

  const load = async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const params = { include_inactive: includeInactive ? 1 : 0 };
      if (q) params.q = q;
      if (role) params.role = role;
      const { data } = await api.get("/team/employees", { params });
      setItems(data.items || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setLoadErr(
        typeof d === "string"
          ? d
          : e?.response?.status
            ? `Could not load team (HTTP ${e.response.status}).`
            : "Could not load team. Check your connection."
      );
      setItems([]);
    }
    finally { setLoading(false); }
  };

  const loadStatus = async () => {
    if (!isHr) return;
    try {
      const { data } = await api.get("/team/simplepay/status");
      setStatus(data);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { if (isHr) load(); /* eslint-disable-next-line */ }, [q, role, includeInactive, isHr]);
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [isHr]);

  if (!user || !isHr) return null;

  return (
    <div className="space-y-4" data-testid="team-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <UsersIcon className="w-6 h-6 text-[#1FB6C1]" /> Team
          </h1>
          <p className="text-xs text-[#647087] mt-0.5">
            {items.length} staff member{items.length === 1 ? "" : "s"} ·
            Owner & admin can edit. Everyone else only sees their own record.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/team/performance"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-performance-link"
            title="Leaderboards, badges & rewards"
          >
            <Trophy className="w-3.5 h-3.5" /> Performance
          </Link>
          <Link
            to="/team/competitions"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-competitions-link"
            title="Competitions, contests & gamification"
          >
            <Trophy className="w-3.5 h-3.5" /> Competitions
          </Link>
          <Link
            to="/recruitment"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-recruitment-link"
            title="Job positions & candidate CVs (HR only)"
          >
            <Briefcase className="w-3.5 h-3.5" /> Recruitment
          </Link>
          <Link
            to="/team/attendance"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-attendance-link"
            title="Full attendance register with HR override"
          >
            <Clock className="w-3.5 h-3.5" /> Attendance
          </Link>
          <Link
            to="/team/leave"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-leave-link"
            title="Approve / deny leave applications"
          >
            <CalendarDays className="w-3.5 h-3.5" /> Leave
          </Link>
          <Link
            to="/team/announcements"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-announcements-link"
            title="Post company-wide announcements"
          >
            <Megaphone className="w-3.5 h-3.5" /> Announce
          </Link>
          <Link
            to="/contracts"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-contracts-link"
            title="Manage contracts and e-signatures"
          >
            <FileSignature className="w-3.5 h-3.5" /> Contracts
          </Link>
          <Link
            to="/clock"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-clocking-link"
            title="Open the Clocking screen (personal or kiosk)"
          >
            <Clock className="w-3.5 h-3.5" /> Clocking
          </Link>
          <Link
            to="/team/simplepay"
            className="hv-btn hv-btn-primary text-xs"
            data-testid="team-simplepay-link"
            title="Match SimplePay employees to SalesFlow users"
          >
            <CloudDownload className="w-3.5 h-3.5" />
            SimplePay
            {status?.totals?.unmatched ? (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-400 text-[#0a0b0e] text-[10px] font-bold px-1">
                {status.totals.unmatched}
              </span>
            ) : null}
          </Link>
          <Link
            to="/team/documents-expiring"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="team-expiring-link"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Expiring docs
          </Link>
        </div>
      </div>

      {/* Search + role chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            className="hv-input text-xs pl-8 pr-7 w-full"
            data-testid="team-search-input"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087]" aria-label="Clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {ROLE_CHIPS.map((r) => (
          <button
            key={r.value || "all"}
            type="button"
            onClick={() => setRole(r.value)}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              role === r.value
                ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]"
                : "border-white/15 text-[#a0aabf] hover:bg-white/5"
            }`}
            data-testid={`team-role-${r.value || "all"}`}
          >
            {r.label}
          </button>
        ))}
        <label className="text-[11px] text-[#a0aabf] inline-flex items-center gap-1 cursor-pointer ml-2">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            data-testid="team-include-inactive"
          />
          Include inactive
        </label>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 bg-white/[0.04] rounded animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="team-empty">
          {loadErr ? (
            <span className="text-rose-300" data-testid="team-load-error">{loadErr}</span>
          ) : (
            "No staff match these filters."
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="team-grid">
          {items.map((e) => (
            <Link
              key={e.user_id || e.id}
              to={`/team/${e.user_id || e.id}`}
              className="hv-card p-3 hover:border-[#1FB6C1]/40 transition-colors"
              data-testid={`team-card-${e.user_id || e.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/5 overflow-hidden flex items-center justify-center shrink-0">
                  {e.avatar_url ? (
                    <img src={e.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[#a0aabf] text-sm font-semibold">{(e.name || "?").slice(0, 2).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white truncate flex items-center gap-1">
                    {e.name || "(no name)"}
                    {e.simplepay_id && (
                      <ShieldCheck className="w-3 h-3 text-emerald-400" title="Linked to SimplePay" />
                    )}
                  </div>
                  <div className="text-[11px] text-[#647087] truncate">{e.email}</div>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-[#a0aabf]">{e.role}</span>
                    {e.job_title && (
                      <span className="text-[10px] text-[#a0aabf] truncate">· {e.job_title}</span>
                    )}
                    {e.is_active === false && (
                      <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 ml-1">Inactive</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
